/*
  <acetate-picker>
  Frame acetates are separate Shopify products, so each swatch is a real link to a
  sibling product. This element keeps those links pointing at the right variant as
  the shopper changes lens colour, and optionally swaps the section in place instead
  of doing a full page load.

  Everything here is progressive enhancement. With JS off, every swatch is still an
  <a href> pointing at the sibling product with the current lens pre-selected.
*/
class AcetatePicker extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector(".acetate-picker__group");
    this.swatches = Array.from(this.querySelectorAll(".acetate-swatch"));
    if (!this.group || this.swatches.length === 0) return;

    this.sectionId = this.dataset.section;
    this.map = this.readMap();
    this.prefetched = new Set();

    this.onKeydown = this.handleKeydown.bind(this);
    this.group.addEventListener("keydown", this.onKeydown);

    if (this.dataset.prefetch === "true") {
      this.onPointerEnter = this.handlePointerEnter.bind(this);
      this.group.addEventListener("pointerenter", this.onPointerEnter, true);
    }

    if (this.dataset.instant === "true") {
      this.onClick = this.handleClick.bind(this);
      this.group.addEventListener("click", this.onClick);
    }

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== this.sectionId) return;
        this.retarget(event.data.variant);
      });
    }
  }

  disconnectedCallback() {
    this.group?.removeEventListener("keydown", this.onKeydown);
    if (this.onPointerEnter) {
      this.group?.removeEventListener("pointerenter", this.onPointerEnter, true);
    }
    if (this.onClick) this.group?.removeEventListener("click", this.onClick);
    this.unsubscribe?.();
  }

  readMap() {
    const el = this.querySelector("[data-acetate-map]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn("[acetate-picker] could not parse variant map", error);
      return {};
    }
  }

  /*
    Which of the newly selected variant's options is the lens colour? Rather than
    trusting an option index (option order is not consistent across these products)
    we match against the keys the Liquid map already printed.
  */
  resolveLens(variant) {
    if (!variant?.options) return null;
    const keys = new Set();
    Object.values(this.map).forEach((lensMap) => {
      Object.keys(lensMap).forEach((key) => keys.add(key));
    });
    return variant.options.find((option) => keys.has(option)) || null;
  }

  retarget(variant) {
    const lens = this.resolveLens(variant);
    if (!lens) return;

    this.swatches.forEach((swatch) => {
      const handle = swatch.dataset.productHandle;
      const baseUrl = swatch.dataset.productUrl;
      const variantId = this.map[handle]?.[lens] ?? null;

      swatch.href = variantId ? `${baseUrl}?variant=${variantId}` : baseUrl;
      swatch.classList.toggle("acetate-swatch--unavailable", !variantId);
    });

    this.prefetched.clear();
  }

  /* ---- keyboard: roving tabindex, arrow keys move between swatches ---- */
  handleKeydown(event) {
    const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;

    const current = document.activeElement;
    const index = this.swatches.indexOf(current);
    if (index === -1) return;

    event.preventDefault();
    let next;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = this.swatches.length - 1;
    else {
      const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
      next = (index + step + this.swatches.length) % this.swatches.length;
    }

    this.swatches.forEach((swatch, i) => {
      swatch.tabIndex = i === next ? 0 : -1;
    });
    this.swatches[next].focus();
  }

  handlePointerEnter(event) {
    const swatch = event.target.closest?.(".acetate-swatch");
    if (!swatch || this.prefetched.has(swatch.href)) return;
    this.prefetched.add(swatch.href);

    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = swatch.href;
    document.head.appendChild(link);
  }

  /* ---- optional: swap the section in place instead of navigating ---- */
  handleClick(event) {
    const swatch = event.target.closest(".acetate-swatch");
    if (!swatch || swatch.classList.contains("acetate-swatch--selected")) return;
    // let the browser handle modified clicks (new tab, download, etc.)
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    this.switchTo(swatch);
  }

  async switchTo(swatch) {
    const href = swatch.href;
    const url = new URL(href, window.location.origin);
    url.searchParams.set("section_id", this.sectionId);

    this.classList.add("acetate-picker--loading");

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), "text/html");

      const infoSource = html.getElementById(`ProductInfo-${this.sectionId}`);
      const infoDestination = document.getElementById(`ProductInfo-${this.sectionId}`);
      const mediaSource = html.querySelector(`[data-section="product-media-${this.sectionId}"]`);
      const mediaDestination = document.querySelector(
        `[data-section="product-media-${this.sectionId}"]`
      );
      if (!infoSource || !infoDestination) throw new Error("section markup not found");

      if (mediaSource && mediaDestination) mediaDestination.innerHTML = mediaSource.innerHTML;
      infoDestination.innerHTML = infoSource.innerHTML;

      window.history.pushState({}, "", href);

      const section = document.getElementById(`shopify-section-${this.sectionId}`);
      if (section && typeof initProductPage === "function") initProductPage(section);

      /*
        Apps that mount into the product section (Virtual Try On, review widgets)
        bind to the markup we just replaced. Give them a hook to re-mount.
      */
      document.dispatchEvent(
        new CustomEvent("acetate:switched", {
          detail: { handle: swatch.dataset.productHandle, url: href, sectionId: this.sectionId },
        })
      );
    } catch (error) {
      console.warn("[acetate-picker] instant switch failed, navigating instead", error);
      window.location.href = href;
    } finally {
      this.classList.remove("acetate-picker--loading");
    }
  }
}

if (!customElements.get("acetate-picker")) {
  customElements.define("acetate-picker", AcetatePicker);
}